import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { AuthorizedRequestArg } from '../signin/auth';
import * as authFetch from '../signin/authFetch';
import { listChecklists } from './checklistsSlice';
import { Checklist, ChecklistItem, ChecklistItemUpdate, ChecklistUpdate } from './types';

interface ChecklistState {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  data: GetChecklistResponse | null;
};

interface DeleteChecklistArg extends AuthorizedRequestArg {
  checklistID: string;
};

interface DeleteChecklistRequest {
  deleteChecklist: DeleteChecklistArg;
};

interface DeleteChecklistResponse {
};

export const deleteChecklist = createAsyncThunk('checklist/deleteChecklist', async (arg: DeleteChecklistArg, { rejectWithValue, dispatch }) => {
  const response = await authFetch.post<DeleteChecklistRequest, DeleteChecklistResponse>(
    `${process.env.REACT_APP_SERVER_ROOT}/Goals`,
    {
      'deleteChecklist': arg,
    },
    arg.authToken,
    dispatch,
  );
  dispatch(listChecklists({ authToken: arg.authToken }));
  if (response.error) {
    return rejectWithValue(response.error);
  }
  return response.data || null;
});

interface DeleteChecklistItemArg extends AuthorizedRequestArg {
  checklistID: string;
  checklistItemID: string;
};

interface DeleteChecklistItemRequest {
  deleteChecklistItem: DeleteChecklistItemArg;
};

interface DeleteChecklistItemResponse {
};

export const deleteChecklistItem = createAsyncThunk('checklist/deleteItem', async (arg: DeleteChecklistItemArg, { rejectWithValue, dispatch }) => {
  const response = await authFetch.post<DeleteChecklistItemRequest, DeleteChecklistItemResponse>(
    `${process.env.REACT_APP_SERVER_ROOT}/Goals`,
    {
      'deleteChecklistItem': arg,
    },
    arg.authToken,
    dispatch,
  );
  if (response.error) {
    return rejectWithValue(response.error);
  }
  return response.data || null;
});

interface GetChecklistArg extends AuthorizedRequestArg {
  checklistID: string;
  includeItems: boolean;
};

interface GetChecklistRequest {
  getChecklist: GetChecklistArg;
};

interface GetChecklistResponse {
  checklist: Checklist;
  items: Array<ChecklistItem>;
};

export const getChecklist = createAsyncThunk('checklist/getChecklist', async (arg: GetChecklistArg, { rejectWithValue, dispatch }) => {
  const response = await authFetch.post<GetChecklistRequest, GetChecklistResponse>(
    `${process.env.REACT_APP_SERVER_ROOT}/Goals`,
    {
      'getChecklist': arg,
    },
    arg.authToken,
    dispatch,
  );
  if (response.error) {
    return rejectWithValue(response.error);
  }
  return response.data || null;
});

interface OrderChecklistItemsArg extends AuthorizedRequestArg {
  checklistID: string;
  checklistItemIDs: Array<string>;
};

interface OrderChecklistItemsRequest {
  orderChecklistItems: OrderChecklistItemsArg;
};

interface OrderChecklistItemsResponse {
};

export const orderChecklistItems = createAsyncThunk('checklist/orderItems', async (arg: OrderChecklistItemsArg, { dispatch }) => {
  const response = await authFetch.post<OrderChecklistItemsRequest, OrderChecklistItemsResponse>(
    `${process.env.REACT_APP_SERVER_ROOT}/Goals`,
    {
      'orderChecklistItems': arg,
    },
    arg.authToken,
    dispatch
  );
  // TODO: Should be a more elegant way to update this data.
  await dispatch(getChecklist({ authToken: arg.authToken, checklistID: arg.checklistID, includeItems: true }));
  return response.data || null;
});

interface WriteChecklistArg extends AuthorizedRequestArg {
  checklist: ChecklistUpdate;
};

interface WriteChecklistRequest {
  writeChecklist: WriteChecklistArg;
};

interface WriteChecklistResponse {
  checklist: Checklist;
};

export const writeChecklist = createAsyncThunk('checklist/writeChecklist', async (arg: WriteChecklistArg, { dispatch }) => {
  const response = await authFetch.post<WriteChecklistRequest, WriteChecklistResponse>(
    `${process.env.REACT_APP_SERVER_ROOT}/Goals`,
    {
      'writeChecklist': arg,
    },
    arg.authToken,
    dispatch
  );
  return response.data || null;
});

interface WriteChecklistItemArg extends AuthorizedRequestArg {
  checklistItem: ChecklistItemUpdate;
};

interface WriteChecklistItemRequest {
  writeChecklistItem: WriteChecklistItemArg;
};

interface WriteChecklistItemResponse {
  checklistItem: ChecklistItem;
};

export const writeChecklistItem = createAsyncThunk('checklist/writeItem', async (arg: WriteChecklistItemArg, { dispatch }) => {
  const response = await authFetch.post<WriteChecklistItemRequest, WriteChecklistItemResponse>(
    `${process.env.REACT_APP_SERVER_ROOT}/Goals`,
    {
      'writeChecklistItem': arg,
    },
    arg.authToken,
    dispatch
  );
  return response.data || null;
});


export const goalSlice = createSlice({
  name: 'checklist',
  initialState: {
    status: 'idle',
    error: null,
    data: null,
  } as ChecklistState,
  reducers: {
  },
  extraReducers: builder => {
    builder.addCase(deleteChecklist.pending, (state, action) => {
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(deleteChecklist.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.error = null;
      state.data = null;
    });
    builder.addCase(deleteChecklist.rejected, (state, action) => {
      console.log('Error', action);
      state.data = null;
      state.status = 'failed';
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || null;
      }
    });

    builder.addCase(getChecklist.pending, (state, action) => {
      state.status = 'loading';
      state.error = null;
      // Clear out state if we're loading a new checklist.
      if (state.data?.checklist.id !== action.meta.arg.checklistID) {
        state.data = null;
      }
    });
    builder.addCase(getChecklist.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.error = null;
      state.data = action.payload;
    });
    builder.addCase(getChecklist.rejected, (state, action) => {
      console.log('Error', action);
      state.status = 'failed';
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || null;
      }
    });

    builder.addCase(orderChecklistItems.pending, (state, action) => {
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(orderChecklistItems.fulfilled, (state, action) => {
      console.log('Success', action.payload);
      state.status = 'succeeded';
      state.error = null;
    });
    builder.addCase(orderChecklistItems.rejected, (state, action) => {
      console.log('Error', action.error.message)
      state.status = 'failed';
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || null;
      }
    });

    builder.addCase(writeChecklist.pending, (state, action) => {
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(writeChecklist.fulfilled, (state, action) => {
      console.log('Success', action.payload);
      state.status = 'succeeded';
      state.error = null;
    });
    builder.addCase(writeChecklist.rejected, (state, action) => {
      console.log('Error', action.error.message)
      state.status = 'failed';
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || null;
      }
    });

    builder.addCase(writeChecklistItem.pending, (state, action) => {
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(writeChecklistItem.fulfilled, (state, action) => {
      console.log('Success', action.payload);
      state.status = 'succeeded';
      state.error = null;
    });
    builder.addCase(writeChecklistItem.rejected, (state, action) => {
      console.log('Error', action.error.message)
      state.status = 'failed';
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || null;
      }
    });
  }
});

export const selectChecklist = (state: RootState) => state.checklist.data?.checklist || null;

export const selectItems = (state: RootState) => state.checklist.data?.items || [];

export default goalSlice.reducer